import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { projectsApi } from '@/utils/api-pb';
import type { Project } from '@/utils/pocketbase';

interface CreateProjectModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: (project: Project) => void;
}

const STAGE_KEYS = ['stages.idea', 'stages.mvp', 'stages.product'] as const;
const STAGE_VALUES = ['Только идея', 'Есть MVP', 'Работающий продукт'] as const;

export function CreateProjectModal({
  open,
  onOpenChange,
  onSuccess,
}: CreateProjectModalProps) {
  const { t } = useTranslation('dashboard');

  const [formData, setFormData] = useState({
    name: '',
    market: t('createModal.marketDefault'),
    stage: 'Только идея' as Project['stage'],
    revenue: '0',
    description: '',
  });

  const [errors, setErrors] = useState<{ name?: boolean; description?: boolean }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const resetForm = () => {
    setFormData({
      name: '',
      market: t('createModal.marketDefault'),
      stage: 'Только идея',
      revenue: '0',
      description: '',
    });
    setErrors({});
    setSubmitError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Валидация
    const newErrors: { name?: boolean; description?: boolean } = {};
    if (!formData.name.trim()) {
      newErrors.name = true;
    }
    if (!formData.description.trim()) {
      newErrors.description = true;
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');

    try {
      const project = await projectsApi.create({
        name: formData.name.trim(),
        market: formData.market.trim() || t('createModal.marketDefault'),
        stage: formData.stage,
        revenue: formData.revenue.trim() || '0',
        description: formData.description.trim(),
      });

      resetForm();
      onSuccess(project);
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to create project:', error);
      // Показываем детальную ошибку
      const errorMessage = error instanceof Error
        ? error.message
        : (error as { message?: string })?.message || t('createModal.unknownError');
      setSubmitError(t('createModal.errorPrefix', { error: errorMessage }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      resetForm();
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-[480px] p-8">
        <DialogHeader className="text-center space-y-4">
          <DialogTitle className="text-[24px] font-bold text-center">
            {t('createModal.title')}
          </DialogTitle>
          <DialogDescription className="text-[14px] text-[#565858] text-center leading-relaxed">
            {t('createModal.description')}
          </DialogDescription>
          <p className="text-[14px] text-[#565858] text-center">
            {t('createModal.hint')}
          </p>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="mt-6 space-y-5">
          {submitError && (
            <div className="rounded-xl bg-red-50 p-3 text-[14px] text-red-600">
              {submitError}
            </div>
          )}

          {/* Название */}
          <div className="space-y-2">
            <Label htmlFor="name" className="text-[14px] text-[#565858]">
              {t('createModal.nameLabel')}
            </Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => {
                setFormData({ ...formData, name: e.target.value });
                if (errors.name) setErrors({ ...errors, name: false });
              }}
              placeholder={t('createModal.namePlaceholder')}
              className={`h-12 rounded-xl border-[#eeeeee] ${
                errors.name ? 'border-red-500 focus:border-red-500' : ''
              }`}
              disabled={isSubmitting}
            />
          </div>

          {/* Рынок */}
          <div className="space-y-2">
            <Label htmlFor="market" className="text-[14px] text-[#565858]">
              {t('createModal.marketLabel')}
            </Label>
            <Input
              id="market"
              value={formData.market}
              onChange={(e) => setFormData({ ...formData, market: e.target.value })}
              className="h-12 rounded-xl border-[#eeeeee]"
              disabled={isSubmitting}
            />
          </div>

          {/* Стадия развития */}
          <div className="space-y-2">
            <Label htmlFor="stage" className="text-[14px] text-[#565858]">
              {t('createModal.stageLabel')}
            </Label>
            <Select
              value={formData.stage}
              onValueChange={(value) =>
                setFormData({ ...formData, stage: value as Project['stage'] })
              }
              disabled={isSubmitting}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STAGE_VALUES.map((stage, idx) => (
                  <SelectItem key={stage} value={stage}>
                    {t(STAGE_KEYS[idx] as string)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[12px] text-[#8c8c8c]">
              {t('createModal.stageHint')}
            </p>
          </div>

          {/* Доход в месяц */}
          <div className="space-y-2">
            <Label htmlFor="revenue" className="text-[14px] text-[#565858]">
              {t('createModal.revenueLabel')}
            </Label>
            <Input
              id="revenue"
              value={formData.revenue}
              onChange={(e) => setFormData({ ...formData, revenue: e.target.value })}
              className="h-12 rounded-xl border-[#eeeeee]"
              disabled={isSubmitting}
            />
            <p className="text-[12px] text-[#8c8c8c]">
              {t('createModal.revenueHint')}
            </p>
          </div>

          {/* Описание */}
          <div className="space-y-2">
            <Label htmlFor="description" className="text-[14px] text-[#565858]">
              {t('createModal.descriptionLabel')}
            </Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => {
                setFormData({ ...formData, description: e.target.value });
                if (errors.description) setErrors({ ...errors, description: false });
              }}
              placeholder={t('createModal.descriptionPlaceholder')}
              rows={4}
              className={`rounded-xl border-[#eeeeee] resize-none ${
                errors.description ? 'border-red-500 focus:border-red-500' : ''
              }`}
              disabled={isSubmitting}
            />
          </div>

          {/* Кнопки */}
          <div className="flex gap-[24px]">
            <button
              type="button"
              onClick={() => handleOpenChange(false)}
              disabled={isSubmitting}
              className="flex-1 bg-transparent border border-[var(--grey-accent,#eeeeee)] border-solid text-[color:var(--black-text,#0d0d0d)] px-[24px] py-[12px] rounded-[16px] font-segoe font-semibold text-[16px] leading-[24px] hover:bg-[#2197f1] hover:text-white hover:border-[#2197f1] transition-colors text-center"
            >
              {t('createModal.close')}
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 bg-[#2197f1] text-white px-[24px] py-[12px] rounded-[16px] font-segoe font-semibold text-[16px] leading-[24px] hover:opacity-90 transition-opacity text-center"
            >
              {isSubmitting ? t('createModal.submitting') : t('createModal.submit')}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
